// Firebase Firestore Data Management
import { db } from '../firebase/config';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  deleteDoc,
} from 'firebase/firestore';

// ========== CATEGORIES ==========

// Default categories
const getDefaultCategories = () => [];

// Get all categories
export const getCategories = async () => {
  try {
    const categoriesRef = collection(db, 'categories');
    const snapshot = await getDocs(categoriesRef);
    
    if (snapshot.empty) {
      // Initialize with default categories
      const defaultCats = getDefaultCategories();
      for (const cat of defaultCats) {
        await setDoc(doc(db, 'categories', cat.id), cat);
      }
      return defaultCats;
    }
    
    const categories = [];
    snapshot.forEach(doc => {
      categories.push({ id: doc.id, ...doc.data() });
    });
    
    return categories;
  } catch (error) {
    console.error('Error getting categories:', error);
    return [];
  }
};

// Save category
export const saveCategory = async (category) => {
  try {
    await setDoc(doc(db, 'categories', category.id), category);
    window.dispatchEvent(new Event('categoriesUpdated'));
    return true;
  } catch (error) {
    console.error('Error saving category:', error);
    throw error;
  }
};

// Delete category
export const deleteCategory = async (categoryId) => {
  try {
    await deleteDoc(doc(db, 'categories', categoryId));
    window.dispatchEvent(new Event('categoriesUpdated'));
    return true;
  } catch (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
};

// Get all subcategories
export const getAllSubcategories = async () => {
  try {
    const categories = await getCategories();
    const subcategories = [];
    
    categories.forEach(category => {
      if (category.subcategories) {
        category.subcategories.forEach(sub => {
          subcategories.push({
            ...sub,
            categoryId: category.id,
            categoryName: category.name
          });
        });
      }
    });
    
    return subcategories;
  } catch (error) {
    console.error('Error getting subcategories:', error);
    return [];
  }
};

// ========== PRODUCTS ==========

// Default products with SIMPLE variant structure
const getDefaultProducts = () => [];

// Get all products
export const getProducts = async () => {
  try {
    const productsRef = collection(db, 'products');
    const snapshot = await getDocs(productsRef);
    
    if (snapshot.empty) {
      // Initialize with default products
      const defaultProds = getDefaultProducts();
      for (const prod of defaultProds) {
        await setDoc(doc(db, 'products', prod.id.toString()), prod);
      }
      return defaultProds;
    }
    
    const products = [];
    snapshot.forEach(doc => {
      products.push({ id: doc.id, ...doc.data() });
    });
    
    return products;
  } catch (error) {
    console.error('Error getting products:', error);
    return [];
  }
};

// Save product
export const saveProduct = async (product) => {
  try {
    await setDoc(doc(db, 'products', product.id.toString()), product);
    window.dispatchEvent(new Event('productsUpdated'));
    return true;
  } catch (error) {
    console.error('Error saving product:', error);
    throw error;
  }
};

// Delete product
export const deleteProduct = async (productId) => {
  try {
    await deleteDoc(doc(db, 'products', productId.toString()));
    window.dispatchEvent(new Event('productsUpdated'));
    return true;
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};

// ========== SETTINGS (Logo) ==========

// Get logo
export const getLogo = async () => {
  try {
    const logoDoc = await getDoc(doc(db, 'settings', 'logo'));
    if (logoDoc.exists()) {
      return logoDoc.data().logoData;
    }
    return null;
  } catch (error) {
    console.error('Error getting logo:', error);
    return null;
  }
};

// Save logo
export const saveLogo = async (logoData) => {
  try {
    await setDoc(doc(db, 'settings', 'logo'), { logoData });
    window.dispatchEvent(new Event('logoUpdated'));
    return true;
  } catch (error) {
    console.error('Error saving logo:', error);
    throw error;
  }
};

// ========== EXPORT/IMPORT ==========

// Export all data
export const exportStoreData = async () => {
  try {
    const [categories, products, logo] = await Promise.all([
      getCategories(),
      getProducts(),
      getLogo()
    ]);

    const data = {
      version: '2.0',
      exportDate: new Date().toISOString(),
      categories: JSON.stringify(categories),
      products: JSON.stringify(products),
      logo: logo
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `iseries-store-backup-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);

    return data;
  } catch (error) {
    console.error('Error exporting data:', error);
    throw error;
  }
};

// Import data
export const importStoreData = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const data = JSON.parse(e.target.result);
        
        if (!data.version || !data.categories || !data.products) {
          throw new Error('Invalid backup file format');
        }

        const categories = JSON.parse(data.categories);
        const products = JSON.parse(data.products);

        // Import categories
        for (const cat of categories) {
          await setDoc(doc(db, 'categories', cat.id), cat);
        }

        // Import products
        for (const prod of products) {
          await setDoc(doc(db, 'products', prod.id.toString()), prod);
        }

        // Import logo
        if (data.logo) {
          await setDoc(doc(db, 'settings', 'logo'), { logoData: data.logo });
        }

        // Trigger updates
        window.dispatchEvent(new Event('productsUpdated'));
        window.dispatchEvent(new Event('categoriesUpdated'));
        window.dispatchEvent(new Event('logoUpdated'));

        resolve(data);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};